import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type {
    MarketResearchDemandAnalysisSearchTrendsListParams,
    MarketResearchAbstractionsClusterizationType,
} from "@similarweb/pro-api-client";

import { useKeywordLists } from "app/modules/demand-analysis/keyword-lists";
import DurationServiceSingleton from "app/services/DurationService";

interface IUseKeywordListsNodeAPIQueryParamsProps {
    isMonthToDate?: boolean;
}

const CLUSTERIZATION_TYPE_NONE: MarketResearchAbstractionsClusterizationType = "None";

export const useKeywordListsNodeAPIQueryParams = ({
    isMonthToDate,
}: IUseKeywordListsNodeAPIQueryParamsProps = {}): MarketResearchDemandAnalysisSearchTrendsListParams => {
    const { keywordLists } = useKeywordLists();
    const {
        params: { duration, comparedDuration, country },
    } = useCurrentStateAndParams();
    const {
        forAPI: { from, to, compareFrom, compareTo, isDaily, isWindow },
    } = DurationServiceSingleton.getDurationData(duration, comparedDuration);
    const queryParams = useMemo(
        () =>
            ({
                from,
                to,
                compareFrom,
                compareTo,
                isDaily,
                isWindow,
                country: country as number,
                keywordListIds: keywordLists.map((keyword) => keyword.keywordListId),
                clusterizationType: CLUSTERIZATION_TYPE_NONE,
                isMonthToDate,
            }) satisfies MarketResearchDemandAnalysisSearchTrendsListParams,
        [from, to, compareFrom, compareTo, country, keywordLists, isDaily, isWindow, isMonthToDate],
    );

    return queryParams;
};

export const serializeSearchTrendsQueryParams = (
    queryParams: MarketResearchDemandAnalysisSearchTrendsListParams,
): string => {
    const entries = Object.entries(queryParams).flatMap(([key, value]) => {
        if (value === undefined) {
            return [];
        }
        if (Array.isArray(value)) {
            return value.map((nestedValue: string) => [key, nestedValue]);
        }
        return [[key, value.toString()]];
    });

    return new URLSearchParams(entries).toString();
};
